import { CartAddEvent } from '@theme/events';

/** One purchase state shared by main, sticky and closing controls. No cosmetic FX or theme-side discounts. */
class RydrProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abort = new AbortController();
    const {signal} = this.abort;
    this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-status]');
    this.format = new Intl.NumberFormat(this.dataset.locale, {style:'currency', currency:this.dataset.currency, currencyDisplay:'narrowSymbol', minimumFractionDigits:0, maximumFractionDigits:2});
    this.kitVariants = JSON.parse(this.querySelector('[data-kit-variants]')?.textContent || '[]');
    this.classList.add('rydr-ready');
    this.form.addEventListener('submit', e => { e.preventDefault(); this.purchase(e.submitter?.dataset.action || this.dataset.primary); }, {signal});
    this.addEventListener('change', e => {
      if(e.target.matches('[data-extra-toggle]')) this.querySelector(`[data-extra="${e.target.dataset.extraToggle}"]`).disabled = !e.target.checked;
      if(e.target.matches('[data-variant="1"]')) {
        const url = new URL(location.href); url.searchParams.set('variant',e.target.value); history.replaceState({},'',url);
        const media = e.target.selectedOptions[0]?.dataset.mediaId; if(media) this.showMedia(media);
      }
      this.update();
    }, {signal});
    this.addEventListener('click', e => {const t=e.target.closest('[data-thumbnail]');if(t) this.showMedia(t.dataset.thumbnail);const policy=e.target.closest('[data-policy-target]');if(policy){const details=document.getElementById(policy.dataset.policyTarget);if(details)details.open=true;}}, {signal});
    document.addEventListener('click', e => {if(e.target.closest('[data-mirror-action]')) this.purchase(this.dataset.primary);}, {signal});
    document.addEventListener('rydr:sync',()=>this.update(),{signal});
    this.checkSticky = () => {
      const sticky = this.querySelector('[data-sticky]'); if(!sticky) return;
      const focused = document.activeElement;
      const blocked = document.querySelector('dialog[open],[aria-modal="true"]') || focused?.matches('input:not([type="radio"]):not([type="checkbox"]),textarea,select,[contenteditable="true"]');
      sticky.hidden = this.querySelector('[data-main-actions]').getBoundingClientRect().bottom >= 0 || !!blocked;
    };
    window.addEventListener('scroll', this.checkSticky, {passive:true, signal});
    window.addEventListener('resize', this.checkSticky, {signal});
    document.addEventListener('focusin', this.checkSticky, {signal});
    document.addEventListener('focusout', this.checkSticky, {signal});
    this.observer=new MutationObserver(this.checkSticky);
    this.observer.observe(document.body,{subtree:true,attributes:true,attributeFilter:['open','aria-modal']});const rydrTwo=this.querySelector('[name="quantity"][value="2"][data-pack-id]:not(:disabled)');if(rydrTwo)rydrTwo.checked=true;
    this.update();
    this.checkSticky();
  }
  disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();this.initialized=false;}
  money(cents){return this.format.format(cents/100);}
  moneyEach(cents){const v=cents/100;if(Number.isInteger(v))return this.format.format(v);return new Intl.NumberFormat(this.dataset.locale,{style:'currency',currency:this.dataset.currency,currencyDisplay:'narrowSymbol',minimumFractionDigits:2,maximumFractionDigits:2}).format(v);}
  get quantity(){return Number(this.querySelector('[name="quantity"]:checked').value);}
  get pack(){return this.querySelector('[name="quantity"]:checked')?.dataset?.packId ? this.querySelector('[name="quantity"]:checked').dataset : null;}
  /** Splits the chosen cameras into Full Kits (k) and plain cameras (r). Kit prices come from the Complete Kit product. */
  plan(){
    const n=this.quantity;
    const kits=this.kitVariants;
    const limit=(select,max)=>{[...select.options].forEach(o=>{const off=Number(o.value)>max;o.disabled=off;o.hidden=off;});if(Number(select.value)>max)select.value=String(max);return Number(select.value);};
    const kitSelect=this.querySelector('[data-kit-count]');
    const k=kitSelect?limit(kitSelect,Math.min(n,kits.length)):0;
    const r=n-k;
    const sd=this.querySelector('[data-sd-count]');
    if(sd){const c=limit(sd,r);this.querySelectorAll('[data-sd-box] [data-extra-slot]').forEach((el,i)=>{el.disabled=i>=c;});}
    let pack=null;
    if(r>1){const option=this.querySelector(`[name="quantity"][value="${r}"][data-pack-id]`);if(option)pack=option.dataset;}
    return {n,k,r,kit:k?kits[k-1]:null,pack};
  }
  update(){
    this.form.inert=Boolean(this.busy);
    const plan=this.plan();
    const qty=plan.n;
    const pack=plan.pack;
    this.querySelectorAll('[data-kit]').forEach(el=>{const active=!pack && Number(el.dataset.kit)<=plan.r;el.hidden=!active;el.querySelector('select').disabled=!active;});
    const selected=[...this.querySelectorAll('[data-variant]:not(:disabled),[data-extra]:not(:disabled)')];
    const valid=(!pack || pack.packAvailable==='true') && (!plan.kit || plan.kit.available===true) && selected.every(el=>el.selectedOptions[0] && !el.selectedOptions[0].disabled);
    const total=selected.reduce((sum,el)=>sum+Number(el.selectedOptions[0]?.dataset.price||0),Number(pack?.packPrice||0)+Number(plan.kit?.price||0));
    const base=Number(this.querySelector('[data-variant="1"]').selectedOptions[0]?.dataset.price||0);
    const offerPrice=n=>{const option=this.querySelector(`[name="quantity"][value="${n}"]`);return option?.dataset.packId ? Number(option.dataset.packPrice) : base*n;};
    this.querySelectorAll('[data-quantity-price]').forEach(el=>{
      const n=Number(el.dataset.quantityPrice);
      const price=offerPrice(n);
      el.textContent=this.money(price);
      const per=this.querySelector(`[data-per-unit="${n}"]`);if(per)per.textContent=this.moneyEach(price/n);
      const separate=this.querySelector(`[data-separate="${n}"]`);
      const saving=this.querySelector(`[data-saving="${n}"]`);
      if(separate){separate.hidden=base*n<=price;separate.querySelector('[data-separate-price]').textContent=this.money(base*n);}
      if(saving){saving.hidden=base*n<=price;saving.querySelector('[data-saving-amount]').textContent=this.money(base*n-price);}
    });
    const kitBox=this.querySelector('[data-kit-box]');
    if(kitBox){
      const sdPrice=Number(this.querySelector('[data-sd-box] [data-extra-slot] option')?.dataset.price||0);
      const full=this.kitVariants[qty-1];
      if(full){
        const per=Math.round((Number(full.price)-offerPrice(qty)-sdPrice*qty)/qty);
        const save=Number(kitBox.dataset.partsTotal||0)-per;
        kitBox.querySelector('[data-kit-line]').textContent=`All 3 for ${this.money(per)} per set`; // price of the 3 accessories; the SD card is shown on the line below
        const badge=kitBox.querySelector('[data-kit-save]');badge.hidden=save<=0;badge.textContent=`Save ${this.money(save)}`;
      }
      kitBox.dataset.on=String(plan.k>0);
      kitBox.querySelector('[data-kit-sub]').textContent=plan.k ? `${plan.k} Full Kit${plan.k>1?'s':''} added. Each includes the camera and SD card.` : `SD card added too (${this.money(sdPrice)} each)`;
    }
    const sdNote=this.querySelector('[data-sd-note]');
    if(sdNote)sdNote.textContent=plan.k ? (plan.r ? 'Full Kits include their card. Add cards for the other cameras.' : 'Full Kits include their SD card.') : 'One per camera. Or use your own.';
    // Every camera is a Full Kit: its SD card is already in the kit, so show that instead of a picker stuck on "No thanks".
    const sdBox=this.querySelector('[data-sd-box]');
    if(sdBox){
      const included=plan.k>0 && plan.r===0;
      sdBox.dataset.included=String(included);
      const sdPick=sdBox.querySelector('[data-sd-count]');if(sdPick)sdPick.hidden=included;
      const tag=sdBox.querySelector('[data-sd-included]');if(tag)tag.hidden=!included;
      const req=this.querySelector('.rydr-requirements');if(req)req.style.display=included?'none':''; // "Add a memory card" line is wrong when the card is in the kit
    }
    const compare=Number(this.querySelector('[data-variant="1"]').selectedOptions[0]?.dataset.compare||0);
    this.querySelectorAll('[data-compare-price]').forEach(el=>{el.hidden=compare<=base;el.textContent=this.money(compare);});
    this.querySelectorAll('[data-discount-pill]').forEach(el=>{el.hidden=compare<=base;el.querySelector('[data-single-saving]').textContent=this.money(compare-base);});
    document.querySelectorAll('[data-total]').forEach(el=>el.textContent=this.money(total));
    document.querySelectorAll('[data-unit-price]').forEach(el=>el.textContent=this.money(base));
    const actionLabel=this.dataset.primary==='buy' && qty>1 ? (this.strings.buyPlural || this.strings.buy) : this.strings[this.dataset.primary];
    document.querySelectorAll('[data-count]').forEach(el=>el.textContent=this.strings.giftMode ? `${qty} ${qty===1?this.strings.giftUnit:this.strings.giftUnits}` : `${this.strings.selection}: ${qty}`);
    document.querySelectorAll('[data-mirror-action]').forEach(el=>{el.disabled=this.busy||this.locked||!valid;el.textContent=actionLabel;});
    this.querySelectorAll('[data-action]').forEach(el=>el.disabled=this.busy||this.locked||!valid);
    const label=this.querySelector('[data-action-label]');if(label)label.textContent=this.busy?this.strings.pending:valid?actionLabel:this.strings.soldOut;
    document.querySelectorAll('[data-return-top]').forEach(el=>el.href=`#${this.id}`);
    const terms=this.querySelector('[data-payment-terms]');
    const multi=qty>1 || plan.k>0 || this.querySelector('[data-extra]:not(:disabled)')!==null;
    if(terms)terms.hidden=multi;
    const note=this.querySelector('[data-multi-payment-note]');if(note)note.hidden=!multi;
    this.total=total;
  }
  items(){
    const gift=this.querySelector('[data-gift]')?.value.trim();const items=[];
    const props=gift?{properties:{'Gift message':gift}}:{};
    const plan=this.plan();
    if(plan.kit) items.push({id:Number(plan.kit.id),quantity:1,...props});
    if(plan.pack) items.push({id:Number(plan.pack.packId),quantity:1,...props});
    else this.querySelectorAll('[data-variant]:not(:disabled)').forEach(el=>{
      const id=Number(el.value);const found=items.find(i=>i.id===id);
      if(found)found.quantity++;
      else items.push({id,quantity:1,...props});
    });
    this.querySelectorAll('[data-extra]:not(:disabled)').forEach(el=>{
      const id=Number(el.value);const found=items.find(i=>i.id===id);
      if(found)found.quantity++;
      else items.push({id,quantity:1});
    });
    return items;
  }
  async purchase(action){
    if(this.busy||this.locked||this.querySelector('[data-action]').disabled)return;
    if(!this.form.reportValidity())return;
    this.busy=true;this.update();this.status.textContent=this.strings.pending;
    const root=window.Shopify.routes.root;
    let mutationStarted=false;
    try{
      const beforeResponse=await fetch(`${root}cart.js`,{headers:{Accept:'application/json'}});
      if(!beforeResponse.ok)throw new Error(this.strings.error);
      const before=await beforeResponse.json();
      const items=this.items();
      const sections=[...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el=>el.dataset.sectionId))].slice(0,5);
      mutationStarted=true;
      const response=await fetch(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections,sections_url:location.pathname})});
      const added=await response.json();
      // A server error may still partially mutate inventory/cart. Never auto-retry an uncertain add.
      if(!response.ok||added.status)throw new Error(added.description||this.strings.error);
      const afterResponse=await fetch(`${root}cart.js`,{headers:{Accept:'application/json'}});
      if(!afterResponse.ok)throw new Error(this.strings.uncertain);
      const after=await afterResponse.json();
      const expected=items.reduce((sum,i)=>sum+i.quantity,0);
      if(after.item_count!==before.item_count+expected)throw new Error(this.strings.uncertain);
      if(action==='buy'){
        // Retain an existing cart and expose its real total before the shopper continues.
        location.assign(before.item_count ? `${root}cart` : `${root}checkout`);
        return;
      }
      this.dispatchEvent(new CartAddEvent(after,this.id,{source:'product-form-component',productId:this.dataset.productId,itemCount:expected,sections:added.sections}));
      this.status.textContent=this.strings.added;
      this.querySelector('[data-review-cart]').hidden=false;
      const drawer=document.querySelector('cart-drawer-component');
      if(drawer?.open)requestAnimationFrame(()=>drawer.open());else location.assign(`${root}cart`);
    }catch(error){
      this.locked=mutationStarted;
      this.status.textContent=`${error.message||this.strings.error}${mutationStarted?' '+this.strings.uncertain:''}`;
      this.querySelector('[data-review-cart]').hidden=false;
      this.status.scrollIntoView({block:'center',behavior:'instant'});
    }finally{this.busy=false;this.update();}
  }
}
if(!customElements.get('rydr-product'))customElements.define('rydr-product',RydrProduct);

// Theme-editor section reloads must synchronise closing controls without adding another purchase state.
document.addEventListener('shopify:section:load',()=>document.dispatchEvent(new Event('rydr:sync')));

// Preserve the product variant and campaign query when using the native market form.
document.addEventListener('submit', event => {
  if(event.target.id !== 'RydrMarketForm') return;
  let field=event.target.querySelector('input[name="return_to"]');
  if(!field){field=document.createElement('input');field.type='hidden';field.name='return_to';event.target.append(field);}
  field.value=location.pathname+location.search;
});
